import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useDrag } from 'react-dnd';

import { getDisplayName, getMatchedSegments } from '../../util/seatingChartUtils';

import './Guest.styles.less';

const Guest = ({
  onStartDrag,
  onEndDrag,
  guest,
  group,
  onSelectGuest,
  isCouple,
  isGroupPrimary,
  isSelected,
  searchState,
  isFiltering,
  partOfDrag,
  searchToken,
  setGroupHover,
}) => {
  // prepare drag information: (when group primary is dragged)
  const key = isCouple ? guest.couple_role : guest.guest_view.id;
  const people = isGroupPrimary ? group.guests.map(g => g.guest_view.id) : [key];

  const [, dragRef] = useDrag({
    item: { type: 'SEAT' },
    begin: () => {
      onStartDrag(people);
      if (isCouple) {
        return {
          item: 'seat',
          seat: guest,
          firstName: guest.first_name,
          lastName: guest.last_name,
          displayName: null,
        };
      }
      // drag data for 'child of'/'guest of':
      const isNameless = guest.guest_view.first_name === null;
      const firstName = isNameless
        ? guest.display_name.substring(0, 8)
        : guest.guest_view.first_name;
      const lastName = isNameless ? guest.display_name.substring(9) : guest.guest_view.family_name;
      return { item: 'seat', seat: guest, firstName, lastName, displayName: null };
    },
    end: () => onEndDrag(),
  });
  const [hoverActive, setHover] = useState(false);
  const seated = guest.table_uuid !== null;
  const isDragging = partOfDrag && !seated;
  const relationshipType = guest.guest_view ? guest.guest_view.relationship_type : null;
  const isLeadGuest = isFiltering || isCouple || relationshipType === 'PRIMARY';

  const hasDeclined = !isCouple ? guest.rsvp_type === 'DECLINED' : false;

  // classnames
  const className = cx('sc__guest-list-item', {
    'guest--selected': isSelected && !seated && !partOfDrag,
    'single--hovering': hoverActive && !isGroupPrimary && !partOfDrag && !seated,
  });
  const classes = cx('sc__guest-list-item__guest', {
    'guest--secondary': !isLeadGuest,
    'guest--relationship': guest.secondary_display_name != null,
    'guest--no-opacity': isDragging,
    'sc__guest-list-item__guest--seated': seated,
  });
  const primaryNameClasses = cx('guest__name-display--primary', {
    'guest__name-display--seated': seated,
    'has-declined with-tooltip tooltip-top': hasDeclined,
  });
  const secondaryNameClasses = cx('guest__name-display--secondary', {
    'guest__name-display--seated': seated,
    'has-declined with-tooltip tooltip-top': hasDeclined,
  });
  const vestigeClass = cx('guest--vestige', {
    'guest--secondary': !isLeadGuest,
    'guest--relationship': guest.secondary_display_name != null,
  });

  // parse display name
  const name = searchState
    ? getMatchedSegments(guest.display_name, searchToken)
    : getDisplayName(guest, isCouple);
  const primaryName = searchState ? (
    <span>
      {name.start}
      <strong>{name.match}</strong>
      {name.end}
    </span>
  ) : (
    <span>
      {name.firstPart} <strong>{name.secondPart}</strong>
    </span>
  );

  // handle hover events
  const handleMouseEnter = () => {
    if (isGroupPrimary && !seated) setGroupHover(true);
    setHover(true);
  };
  const handleMouseLeave = () => {
    if (setGroupHover) setGroupHover(false);
    setHover(false);
  };

  return (
    <div className={className} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {isDragging && (
        <div className={vestigeClass}>
          <div className={primaryNameClasses}>{primaryName}</div>
          {guest.secondary_display_name != null && (
            <div className={secondaryNameClasses}>{guest.secondary_display_name}</div>
          )}
        </div>
      )}
      <div
        ref={seated ? null : dragRef}
        className={classes}
        onClick={() => !seated && onSelectGuest(guest, group, isCouple)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {seated && <i className="zolaicon zolaicon-checkmark sc__guest__icon" />}
        <div
          className={primaryNameClasses}
          data-tooltip="This guest has declined attending this event"
        >
          <span className="primary-name__text">{primaryName}</span>
          {hasDeclined && <span className="zolaicon zolaicon-clear-field" />}
        </div>
        {guest.secondary_display_name != null && (
          <div
            className={secondaryNameClasses}
            data-tooltip="This guest has declined attending this event"
          >
            {guest.secondary_display_name}
          </div>
        )}
      </div>
    </div>
  );
};

Guest.propTypes = {
  guest: PropTypes.shape({
    guest_view: PropTypes.shape({
      family_name: PropTypes.string,
      first_name: PropTypes.string,
      relationship_type: PropTypes.string,
    }),
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    display_name: PropTypes.string,
    secondary_display_name: PropTypes.string,
    table_uuid: PropTypes.string,
  }),
  group: PropTypes.shape({
    guest_group_id: PropTypes.number,
    guests: PropTypes.arrayOf(PropTypes.shape({})),
    guest_affiliation: PropTypes.string,
  }),
  isCouple: PropTypes.bool,
  searchState: PropTypes.bool,
  isFiltering: PropTypes.bool,
  partOfDrag: PropTypes.bool,
  searchToken: PropTypes.string,
  onSelectGuest: PropTypes.func,
  onStartDrag: PropTypes.func,
  onEndDrag: PropTypes.func,
  setGroupHover: PropTypes.func,
};

export default Guest;



// WEBPACK FOOTER //
// ./src/components/manage/seatingchart/GuestList/Guest/Guest.jsx